// DOM-overlay projection layer, extracted from GameBoard.jsx. The 3D
// scene is the source of truth; these functions project world-space
// positions (cards, cells, piles) into canvas-local DOM coordinates
// every frame and position the absolutely-placed overlay elements
// (hover status pills, equipment stacks, movement-reach quads, cell
// element pips, equip/cell/projectile-path targets, start-draw
// buttons). register*Ref functions capture the overlay DOM nodes the
// projections write to.
//
// Per the UI scaling architecture, all coordinates are computed in
// canvas-local space (subtract the .app-canvas rect) and clamped to
// canvas dims, never window dims.

import { UI } from '../../../utils/arena/uiSounds';
import { CARD_HEIGHT, createLifeHUD } from '../../../utils/game/cardMesh';
import { projectilePaths } from '../../../utils/game/engine/automation';
import { displayStatusKeys } from './equipActions';
import { cellsReachable, cellsWithAnySite, connectedSubsurfaceComponents, countsAsUnit, elementsAtCell, hasSummoningSickness, sampleCellGeometry } from '../../../utils/game/engine/core';
import { computeGridPositions } from '../../../utils/game/grid';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { resolveLocalImageUrl } from '../../../utils/localApi';
import EquipmentStacks from '../EquipmentStacks';
import HoverEquippedCards from '../HoverEquippedCards';

// Art for a carried card. Equipped entries are the ONLY card render path that
// never went through instanceFromMirror / resolveLocalImageUrl: the bearer's
// equippedCards arrive over the wire exactly as the equipping client stored
// them, so on web a desktop peer's `/sorcery-images/<slug>` path pointed at a
// file only its own Electron server can serve (Pages answers with index.html,
// the <img> fails, and the stack shows a blank card with the name under it).
// An entry that arrives without an imageUrl (stripped on the wire, or stored
// before the field existed) falls back to the catalog printing. Resolve like
// every other renderer does.
export function equippedCardArt(host, e) {
  const raw = e?.imageUrl
    || (e?.cardId != null ? host._getFullCard?.(e.cardId)?.printings?.[0]?.image_url : null);
  return resolveLocalImageUrl(raw) || null;
}

/**
 * Project a card mesh's world position to canvas-local screen pixels.
 * Used by the marquee hit test and any future UI that needs to sit on
 * top of a 3D card.
 */
export function worldPositionToScreen(host, worldPos) {
  if (!host.scene?.camera || !host.canvasRef.current) return null;
  const v = host._hugVec.copy(worldPos).project(host.scene.camera);
  const rect = host.canvasRef.current.getBoundingClientRect();
  return {
    x: ((v.x + 1) / 2) * rect.width + rect.left,
    y: ((-v.y + 1) / 2) * rect.height + rect.top,
  };
}

export function registerStartDrawRefs(host, refs) {
  host._startDrawRefs = refs;
}

// Project the active draw-phase prompt's pile meshes to screen
// coordinates each frame and lock the Draw a Spell / Draw a Site
// buttons over the matching piles. Per-pile screen size also
// drives a CSS scale multiplier so the buttons track 3D camera
// zoom. Fully zoomed out, the buttons shrink down to ~minScale
// instead of dwarfing the pile.
export function updateStartDrawOverlays(host) {
  const refs = host._startDrawRefs;
  if (!refs) return;
  const hide = () => {
    for (const r of Object.values(refs)) {
      if (r?.current) {
        r.current.style.display = 'none';
        r.current.style.opacity = '0';
      }
    }
  };
  const prompt = host._localDrawPhaseChoicePrompt();
  if (!prompt) { hide(); return; }
  const camera = host.scene?.camera;
  const canvas = host.canvasRef.current;
  if (!camera || !canvas) { hide(); return; }
  const rect = canvas.getBoundingClientRect();
  const spellMesh = prompt._spellbookPileId ? host.pileMeshes.get(prompt._spellbookPileId) : null;
  const atlasMesh = prompt._atlasPileId ? host.pileMeshes.get(prompt._atlasPileId) : null;
  // Reference width = the projected pile width the button is sized
  // to fit over at scale 1.0. 5% of the canvas matches a typical
  // mid-zoom pile rendering on a 1600-wide canvas.
  const REFERENCE_PILE_WIDTH = rect.width * 0.05;
  const MIN_SCALE = 0.35;
  const MAX_SCALE = 0.75;
  // Anchor the button at the lower third of the pile's projected
  // footprint so the label sits over the bottom of the cards
  // rather than the middle of the stack.
  const BOTTOM_THIRD = 2 / 3;
  const projectAndScale = (mesh) => {
    if (!mesh) return null;
    const box = host._hugBox.setFromObject(mesh);
    const midX = (box.min.x + box.max.x) / 2;
    const midZ = (box.min.z + box.max.z) / 2;
    const topY = box.max.y;
    // Canvas-local coordinates: the buttons are position:fixed inside
    // .app-canvas, a transform-containing block, so adding rect.left/top
    // would shift them by the letterbox offset (see updateHoverStatusOverlayPosition).
    const projectWorld = (x, y, z) => {
      const v = host._hugVec.set(x, y, z).project(camera);
      return {
        x: ((v.x + 1) / 2) * rect.width,
        y: ((-v.y + 1) / 2) * rect.height,
      };
    };
    // Horizontal anchor = pile center projection. Front/back of
    // the pile project to different X under a tilted camera, so
    // lerping along those edges drifts horizontally; the pile's
    // own center is the only horizontally-honest reference.
    const center = projectWorld(midX, topY, midZ);
    // Vertical anchor walks the pile's Z axis: pick whichever edge
    // projects lower on screen, then lerp the Y component only.
    const zFront = projectWorld(midX, topY, box.min.z);
    const zBack  = projectWorld(midX, topY, box.max.z);
    const bottomY = Math.max(zFront.y, zBack.y);
    const topY_screen = Math.min(zFront.y, zBack.y);
    const cx = center.x;
    const cy = topY_screen + (bottomY - topY_screen) * BOTTOM_THIRD;
    // Pile world-AABB → screen width gives a real measurement of
    // how big the pile renders right now, accounting for camera
    // angle, distance, and the pile's own rotation.
    const leftPx  = projectWorld(box.min.x, topY, midZ).x;
    const rightPx = projectWorld(box.max.x, topY, midZ).x;
    const pileWidth = Math.abs(rightPx - leftPx);
    const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pileWidth / REFERENCE_PILE_WIDTH));
    return { x: cx, y: cy, scale };
  };
  const place = (ref, screen) => {
    const el = ref?.current;
    if (!el) return;
    if (!screen) {
      el.style.display = 'none';
      el.style.opacity = '0';
      return;
    }
    el.style.display = 'block';
    el.style.left = `${screen.x}px`;
    el.style.top = `${screen.y}px`;
    el.style.transform = `translate(-50%, -50%) scale(${screen.scale})`;
    el.style.opacity = '1';
  };
  place(refs.spell, projectAndScale(spellMesh));
  place(refs.atlas, projectAndScale(atlasMesh));
}

/**
 * Recompute the persistent "hug" rectangle that wraps every selected
 * card, writing both the screen-space bounds (for hit testing) and
 * the DOM overlay style (for rendering). Called every frame by the
 * scene tick so the rect follows cards as they move, as well as from
 * updateSelectionHighlights() the moment the selection changes so
 * the user gets instant feedback.
 *
 * The bounds are computed by projecting each selected card's world-
 * space AABB corners through the camera and taking min/max of the
 * resulting screen coordinates. That handles tapped / rotated cards
 * correctly. We always hug the oriented shape, not an ideal
 * rectangle.
 *
 * A generous padding is applied so the outline doesn't touch card
 * edges; that makes the rounded-corner styling read as a halo rather
 * than a tight frame.
 */
export function updateHugRect(host) {
  const el = host.hugRectRef.current;
  if (!el) return;

  const highlighted = host.collectHighlightedCardIds();
  if (highlighted.size === 0 || !host.scene?.camera || !host.canvasRef.current) {
    el.style.display = 'none';
    host.hugRectScreen = null;
    return;
  }

  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let any = false;

  const box = host._hugBox;
  const v = host._hugVec;

  for (const cardId of highlighted) {
    const mesh = host.meshes.get(cardId);
    if (!mesh) continue;
    box.setFromObject(mesh);
    if (!isFinite(box.min.x) || box.isEmpty()) continue;

    // Project all 8 corners of the world-space AABB.
    const mn = box.min, mx = box.max;
    const corners = [
      [mn.x, mn.y, mn.z], [mx.x, mn.y, mn.z],
      [mn.x, mx.y, mn.z], [mx.x, mx.y, mn.z],
      [mn.x, mn.y, mx.z], [mx.x, mn.y, mx.z],
      [mn.x, mx.y, mx.z], [mx.x, mx.y, mx.z],
    ];
    for (const [x, y, z] of corners) {
      v.set(x, y, z).project(camera);
      const sx = ((v.x + 1) / 2) * rect.width + rect.left;
      const sy = ((-v.y + 1) / 2) * rect.height + rect.top;
      if (sx < minX) minX = sx;
      if (sy < minY) minY = sy;
      if (sx > maxX) maxX = sx;
      if (sy > maxY) maxY = sy;
      any = true;
    }
  }

  if (!any) {
    el.style.display = 'none';
    host.hugRectScreen = null;
    return;
  }

  // Pad the outline so it reads as a halo around the cards rather
  // than a tight frame pressed into them. The padding is the SAME
  // value we test against in hit detection, so clicks that land just
  // outside the visual edge still register as "inside the hug zone".
  const padding = 10;
  minX -= padding;
  minY -= padding;
  maxX += padding;
  maxY += padding;

  host.hugRectScreen = { minX, minY, maxX, maxY };
  el.style.display = 'block';
  el.style.left = `${minX}px`;
  el.style.top = `${minY}px`;
  el.style.width = `${maxX - minX}px`;
  el.style.height = `${maxY - minY}px`;
}

/**
 * Screen-space bounding rect of a single mesh in viewport coords,
 * via the same 8-corner AABB projection updateHugRect uses (so
 * tapped / rotated cards hug correctly). Null while the camera or
 * canvas isn't ready. Used by the first-unit tutorial spotlight.
 */
export function projectMeshScreenRect(host, mesh) {
  if (!mesh || !host.scene?.camera || !host.canvasRef?.current) return null;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const box = host._hugBox;
  const v = host._hugVec;
  box.setFromObject(mesh);
  if (!isFinite(box.min.x) || box.isEmpty()) return null;
  const mn = box.min, mx = box.max;
  const corners = [
    [mn.x, mn.y, mn.z], [mx.x, mn.y, mn.z],
    [mn.x, mx.y, mn.z], [mx.x, mx.y, mn.z],
    [mn.x, mn.y, mx.z], [mx.x, mn.y, mx.z],
    [mn.x, mx.y, mx.z], [mx.x, mx.y, mx.z],
  ];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y, z] of corners) {
    v.set(x, y, z).project(camera);
    const sx = ((v.x + 1) / 2) * rect.width + rect.left;
    const sy = ((-v.y + 1) / 2) * rect.height + rect.top;
    if (sx < minX) minX = sx;
    if (sy < minY) minY = sy;
    if (sx > maxX) maxX = sx;
    if (sy > maxY) maxY = sy;
  }
  return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Sync the hoverStatusOverlay state with the currently-hovered card's
 * active statuses + ATK / DEF MODIFIERS (signed deltas vs. the
 * printed base). Called whenever hover identity changes AND whenever
 * the hovered card's statuses or stats mutate. The state value is
 * treated as immutable (statuses are snapshot-copied) so Preact
 * re-renders even though cardInstance.statuses is the same array
 * reference.
 *
 * Stat pills only surface when the live value differs from the base
 * An unbuffed 5/5 minion shows nothing, but +1/-2 modifiers light
 * up immediately. The overlay opens whenever EITHER status effects
 * or non-zero stat deltas are present.
 */
export function syncHoverStatusOverlay(host) {
  const mesh = host.hoveredMesh;
  const card = mesh?.userData?.type === 'card' ? mesh.userData.cardInstance : null;
  // Effective status union (v1.16a): manual + aura + printed,
  // suppressed to just 'disabled' if the card is disabled, plus the
  // equipment-derived Lance pseudo-status. The hover overlay shows
  // what the card EFFECTIVELY has, not just what was manually toggled.
  const statuses = card ? displayStatusKeys(card) : [];
  const hasStats = !!card && card.currentAttack !== undefined;
  const atkDelta = hasStats ? (card.currentAttack || 0) - (card.baseAttack || 0) : 0;
  const defDelta = hasStats ? (card.currentLife || 0) - (card.baseLife || 0) : 0;
  const attackDelta = atkDelta !== 0 ? atkDelta : null;
  const defenseDelta = defDelta !== 0 ? defDelta : null;
  const equipped = card?.equippedCards || [];
  const next = (card && (statuses.length > 0 || attackDelta != null || defenseDelta != null || equipped.length > 0))
    ? {
      cardId: card.id,
      statuses: [...statuses],
      attackDelta,
      defenseDelta,
      equippedCards: equipped.map((eq) => ({ ...eq, imageUrl: equippedCardArt(host, eq) })),
    }
    : null;
  const cur = host.state.hoverStatusOverlay;
  const sameEquipment = cur && next
    && cur.equippedCards?.length === next.equippedCards.length
    && cur.equippedCards.every((eq, i) => eq.id === next.equippedCards[i].id);
  const same = (cur && next
    && cur.cardId === next.cardId
    && cur.statuses.length === next.statuses.length
    && cur.statuses.every((s, i) => s === next.statuses[i])
    && cur.attackDelta === next.attackDelta
    && cur.defenseDelta === next.defenseDelta
    && sameEquipment);
  if (same || (!cur && !next)) return;
  host.setState({ hoverStatusOverlay: next });
}

/**
 * Walk every mesh on the table and rebuild the equipmentOverlays
 * state array with one entry per host card that currently has
 * attached equipment. Drives the permanent EquipmentStacks overlay.
 *
 * Shallow-equality short-circuits so noisy callers (the per-frame
 * loop or rapid IPC bursts) don't trigger React re-renders unless
 * equipment identity actually changes. Position updates flow through
 * the ref-based per-frame tick, not state.
 */
export function syncEquipmentOverlays(host) {
  const entries = [];
  for (const [cardId, mesh] of host.meshes) {
    if (mesh?.userData?.type !== 'card') continue;
    const card = mesh.userData.cardInstance;
    const eq = card?.equippedCards;
    if (!eq || eq.length === 0) continue;
    entries.push({
      cardId,
      equippedCards: eq.map((e) => ({
        id: e.id,
        name: e.name,
        imageUrl: equippedCardArt(host, e),
        isSite: !!e.isSite,
      })),
    });
  }
  const cur = host.state.equipmentOverlays;
  const same = cur.length === entries.length && cur.every((c, i) => (
    c.cardId === entries[i].cardId
    && c.equippedCards.length === entries[i].equippedCards.length
    && c.equippedCards.every((e, j) => e.id === entries[i].equippedCards[j].id)
  ));
  if (same) return;
  host.setState({ equipmentOverlays: entries });
}

export function registerEquipmentOverlayRef(host, cardId, el) {
  if (!el) {
    host.equipmentOverlayRefs.delete(cardId);
    return;
  }
  host.equipmentOverlayRefs.set(cardId, el);
}

/**
 * Project the hovered card's screen-space bounds and pin the two
 * DOM overlays there:
 *   - status badge column at the card's top-right corner
 *   - ATK / DEF stat pill row centered above the card's top edge
 *
 * Runs every frame so both overlays track camera pans and
 * physics-driven card motion. Constant-pixel sizing falls out of
 * the DOM. No scaling logic needed.
 */
export function updateHoverStatusOverlayPosition(host) {
  const badgesEl = host.hoverBadgesRef.current;
  const pillsEl = host.hoverStatPillsRef.current;
  const equipmentEl = host.hoverEquipmentRef.current;
  if (!badgesEl && !pillsEl && !equipmentEl) return;
  const overlay = host.state.hoverStatusOverlay;
  if (!overlay) return;
  const mesh = host.hoveredMesh;
  if (!mesh || mesh.userData?.cardInstance?.id !== overlay.cardId) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;

  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const box = host._hugBox.setFromObject(mesh);
  if (!isFinite(box.min.x) || box.isEmpty()) return;

  const v = host._hugVec;
  const mn = box.min, mx = box.max;
  const corners = [
    [mn.x, mn.y, mn.z], [mx.x, mn.y, mn.z],
    [mn.x, mx.y, mn.z], [mx.x, mx.y, mn.z],
    [mn.x, mn.y, mx.z], [mx.x, mn.y, mx.z],
    [mn.x, mx.y, mx.z], [mx.x, mx.y, mx.z],
  ];
  let minX = Infinity, maxX = -Infinity, minY = Infinity;
  // The overlays are position:fixed inside .app-canvas, which is a
  // transform-containing block (see CLAUDE.md "UI Scaling
  // Architecture"). So their transform values are in canvas-local
  // coordinates, NOT viewport coordinates. Adding rect.left/top here
  // would shift the overlays by the letterbox offset on any setup
  // where the canvas doesn't fill the viewport (ultrawide monitors,
  // non-16:9 fullscreen). That's the "moves around depending on
  // screen size" bug.
  for (const [x, y, z] of corners) {
    v.set(x, y, z).project(camera);
    const sx = ((v.x + 1) / 2) * rect.width;
    const sy = ((-v.y + 1) / 2) * rect.height;
    if (sx < minX) minX = sx;
    if (sx > maxX) maxX = sx;
    if (sy < minY) minY = sy;
  }
  const gap = 8;
  if (badgesEl) {
    badgesEl.style.transform = `translate(${Math.round(maxX + gap)}px, ${Math.round(minY)}px)`;
  }
  if (pillsEl) {
    // The pill row's own static transform shifts by (-50%, -100%),
    // so this anchor sets its bottom-center to (cardCenterX, cardTop
    // − gap). Result: row sits gap px above the card, horizontally
    // centered on it.
    const centerX = (minX + maxX) / 2;
    pillsEl.style.transform = `translate(${Math.round(centerX)}px, ${Math.round(minY - gap)}px) translate(-50%, -100%)`;
  }
  if (equipmentEl) {
    // Anchor the hover stack's top-right at the host card's top-
    // left so its items (align-items: flex-end inside the slide
    // wrapper) align flush against the card's left edge. The
    // inner .hover-equipped-slide wrapper handles the entry/exit
    // animation independently of this per-frame transform.
    equipmentEl.style.transform = `translate(${Math.round(minX - gap)}px, ${Math.round(minY)}px) translate(-100%, 0)`;
  }
}

/**
 * Per-frame projector for every host card's
 * mini equipped-card stack. Anchors each at the host's projected center.
 *
 * Size is driven by --eqstack-card-w/h set per frame so the badge
 * stays proportional to the host across pan and zoom. data-hovered
 * drives the CSS cross-fade with HoverEquippedCards.
 */
export function updateEquipmentOverlaysPosition(host) {
  if (host.equipmentOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const hoveredId = host.hoveredMesh?.userData?.cardInstance?.id ?? null;

  for (const [cardId, el] of host.equipmentOverlayRefs) {
    const mesh = host.meshes.get(cardId);
    if (!mesh) {
      if (el.style.display !== 'none') el.style.display = 'none';
      continue;
    }
    const box = host._hugBox.setFromObject(mesh);
    if (!isFinite(box.min.x) || box.isEmpty()) continue;
    const v = host._hugVec;
    const mn = box.min;
    const mx = box.max;
    const corners = [
      [mn.x, mn.y, mn.z], [mx.x, mn.y, mn.z],
      [mn.x, mx.y, mn.z], [mx.x, mx.y, mn.z],
      [mn.x, mn.y, mx.z], [mx.x, mn.y, mx.z],
      [mn.x, mx.y, mx.z], [mx.x, mx.y, mx.z],
    ];
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [x, y, z] of corners) {
      v.set(x, y, z).project(camera);
      const sx = ((v.x + 1) / 2) * rect.width;
      const sy = ((-v.y + 1) / 2) * rect.height;
      if (sx < minX) minX = sx;
      if (sx > maxX) maxX = sx;
      if (sy < minY) minY = sy;
      if (sy > maxY) maxY = sy;
    }
    const cardW = Math.max(1, maxX - minX);
    const cardH = Math.max(1, maxY - minY);
    if (el.style.display === 'none') el.style.display = 'block';
    el.style.setProperty('--eqstack-card-w', `${cardW.toFixed(2)}px`);
    el.style.setProperty('--eqstack-card-h', `${cardH.toFixed(2)}px`);
    // Anchor at the host's center. The trailing translate(-50%, -50%)
    // centers the mini stack on that anchor.
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    el.style.transform = `translate(${centerX.toFixed(2)}px, ${centerY.toFixed(2)}px) translate(-50%, -50%)`;
    const hovered = hoveredId === cardId ? 'true' : 'false';
    if (el.dataset.hovered !== hovered) el.dataset.hovered = hovered;
  }
}

export function isPointInsideHugRect(host, clientX, clientY) {
  const r = host.hugRectScreen;
  if (!r) return false;
  return clientX >= r.minX && clientX <= r.maxX && clientY >= r.minY && clientY <= r.maxY;
}

/**
 * True if the given viewport point is over the hand container.
 * used by handleMouseUp to pick up a dragged card into the hand
 * instead of dropping it on the table.
 */
export function isPointOverHand(host, clientX, clientY) {
  const hand = document.querySelector('[data-tutorial="board-hand"]');
  if (!hand) return false;
  const r = hand.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

// Is the cursor over a specific unit-hand badge (the owner unit's
// profile)? Lets a dragged owner-locked spell be dropped back onto its
// origin to cancel the cast.
export function isPointOverUnitBadge(host, ownerId, clientX, clientY) {
  if (!ownerId) return false;
  const el = document.querySelector(`[data-unit-badge="${ownerId}"]`);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

// Project a card mesh's OBB to screen coordinates and return the
// top-right corner with a small horizontal gap. Used by the
// double-click action menu to anchor flush with the top of the
// card.
export function projectCardRightEdge(host, mesh) {
  if (!mesh || !host.scene?.camera || !host.canvasRef.current) return null;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const box = host._hugBox.setFromObject(mesh);
  if (!isFinite(box.min.x) || box.isEmpty()) return null;
  const v = host._hugVec;
  const corners = [
    [box.min.x, box.min.y, box.min.z], [box.max.x, box.min.y, box.min.z],
    [box.min.x, box.max.y, box.min.z], [box.max.x, box.max.y, box.min.z],
    [box.min.x, box.min.y, box.max.z], [box.max.x, box.min.y, box.max.z],
    [box.min.x, box.max.y, box.max.z], [box.max.x, box.max.y, box.max.z],
  ];
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [x, y, z] of corners) {
    v.set(x, y, z).project(camera);
    const sx = ((v.x + 1) / 2) * rect.width;
    const sy = ((-v.y + 1) / 2) * rect.height;
    if (sx < minX) minX = sx;
    if (sx > maxX) maxX = sx;
    if (sy < minY) minY = sy;
    if (sy > maxY) maxY = sy;
  }
  return {
    cardLeft: rect.left + minX,
    cardRight: rect.left + maxX,
    cardTop: rect.top + minY,
    cardBottom: rect.top + maxY,
  };
}

// ── Ctrl+click equip-pending selection ─────────────────────────
export function registerEquipOverlayRef(host, id, kind, el) {
  if (!el) {
    const entry = host.equipOverlayRefs.get(id);
    if (entry) {
      delete entry[kind];
      if (!entry.outline && !entry.label) host.equipOverlayRefs.delete(id);
    }
    return;
  }
  const entry = host.equipOverlayRefs.get(id) || {};
  entry[kind] = el;
  host.equipOverlayRefs.set(id, entry);
}


export function registerCellTargetOverlayRef(host, cellKey, el) {
  if (!el) {
    host.cellTargetOverlayRefs.delete(cellKey);
    return;
  }
  host.cellTargetOverlayRefs.set(cellKey, el);
}

export function registerCellMentionOverlayRef(host, cellKey, el) {
  if (!el) {
    host.cellMentionOverlayRefs.delete(cellKey);
    return;
  }
  host.cellMentionOverlayRefs.set(cellKey, el);
}

export function registerProjectilePathOverlayRef(host, refKey, el) {
  if (!el) {
    host.projectilePathOverlayRefs.delete(refKey);
    return;
  }
  host.projectilePathOverlayRefs.set(refKey, el);
}

export function registerCellPipOverlayRef(host, stripKey, el) {
  if (!el) {
    host.cellPipOverlayRefs.delete(stripKey);
    return;
  }
  host.cellPipOverlayRefs.set(stripKey, el);
}

export function registerMovementReachOverlayRef(host, cellKey, el) {
  if (!el) {
    host.movementReachOverlayRefs.delete(cellKey);
    return;
  }
  host.movementReachOverlayRefs.set(cellKey, el);
}

// v1.32c. Union of cells reachable by every dragged minion. The
// halo set is the OR across all dragged units (group drag includes
// every member's destinations). Returns an empty Set when no drag
// is active or no member has a precomputed reach.
export function buildMovementReachCells(host) {
  const reach = host.dragging?.reach;
  if (!reach || reach.size === 0) return new Set();
  // A manual session paints no reach halos. Where a card may legally go is
  // the players' call, not the engine's.
  if (!host._automationsEnabled()) return new Set();
  const union = new Set();
  for (const [, entry] of reach) {
    if (entry.kind === 'spawn') {
      for (const key of entry.cells) union.add(key);
    } else if (entry.kind === 'move') {
      for (const key of cellsReachable(entry.reach)) union.add(key);
    } else if (entry.kind === 'aura') {
      // An aura's legal-cell surface is the same whether it's being
      // played from hand or repositioned on the board.
      // All three aura shapes union their per-anchor covered cells
      // into `entry.cells`. The per-frame projector picks one anchor
      // (nearest to cursor) and flags its covered cells with
      // `data-aura-active="true"` so the player sees exactly which
      // cells the aura would occupy if dropped here. The cells
      // belonging to OTHER anchors stay dim as "also-legal-here."
      if (entry.cells) {
        for (const key of entry.cells) union.add(key);
      }
    }
  }
  return union;
}

// v1.111. Subset of reach cells that should render in Harbinger
// purple instead of the default green. _computeDragReach stamps a
// `harbingerCells` Set on every spawn-entry whose card is a Minion
// that Harbinger's discount applies to. The subset can be all of
// the entry's cells (player can only afford via Harbinger) OR just
// the marked squares within a larger legal set (player can afford
// anywhere but gets a discount on the marks).
export function buildHarbingerReachCells(host) {
  const reach = host.dragging?.reach;
  if (!reach || reach.size === 0) return null;
  if (!host._automationsEnabled()) return null;
  const out = new Set();
  for (const [, entry] of reach) {
    if (entry.kind !== 'spawn' || !entry.harbingerCells) continue;
    for (const key of entry.harbingerCells) out.add(key);
  }
  return out.size > 0 ? out : null;
}

// Sir Perceval's remembered destination cell. Painted amber while he
// is dragged as a reminder of where the opponent's Genesis pick wants
// him to enter. NOT a legal-move marker. It renders regardless of
// reachability or the move-highlight toggle, and is absent from the
// reach map so it never makes an illegal drop legal.
export function buildPercevalTargetCells(host) {
  if (!host.dragging?.members) return null;
  const out = new Set();
  for (const m of host.dragging.members) {
    const cell = m.cardInstance?._percevalTargetCell;
    if (cell) out.add(`${cell.col},${cell.row}`);
  }
  return out.size > 0 ? out : null;
}

// Teal counterpart to buildHarbingerReachCells: the subset of reach
// cells coming from a dragon-portal discount (Dragonlord's Lair /
// Caelestis). Painted teal to match the portal shader instead of the
// Harbinger purple.
export function buildDragonReachCells(host) {
  const reach = host.dragging?.reach;
  if (!reach || reach.size === 0) return null;
  if (!host._automationsEnabled()) return null;
  const out = new Set();
  for (const [, entry] of reach) {
    if (entry.kind !== 'spawn' || !entry.dragonCells) continue;
    for (const key of entry.dragonCells) out.add(key);
  }
  return out.size > 0 ? out : null;
}

export function buildTownReachCells(host) {
  const reach = host.dragging?.reach;
  if (!reach || reach.size === 0) return null;
  if (!host._automationsEnabled()) return null;
  const out = new Set();
  for (const [, entry] of reach) {
    if (entry.kind !== 'spawn' || !entry.townCells) continue;
    for (const key of entry.townCells) out.add(key);
  }
  return out.size > 0 ? out : null;
}

// Project a cell's four world-space corners (tl, tr, br, bl) to
// viewport pixels via the live camera. Returns the projected points
// plus the axis-aligned bounding box, or null if the cell has no
// geometry. Shared by every cell-halo projector below so the visible
// shape always matches the actual projected quadrilateral.
export function projectCellQuad(host, grid, col, row, camera, rect) {
  const sample = sampleCellGeometry(grid, col, row);
  if (!sample) return null;
  const v = host._hugVec;
  const Y = 0.2;
  const worldPts = [
    [sample.tl.x, sample.tl.z],
    [sample.tr.x, sample.tr.z],
    [sample.br.x, sample.br.z],
    [sample.bl.x, sample.bl.z],
  ];
  const points = new Array(4);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < 4; i++) {
    const [x, z] = worldPts[i];
    v.set(x, Y, z).project(camera);
    const sx = ((v.x + 1) / 2) * rect.width;
    const sy = ((-v.y + 1) / 2) * rect.height;
    points[i] = { x: sx, y: sy };
    if (sx < minX) minX = sx;
    if (sx > maxX) maxX = sx;
    if (sy < minY) minY = sy;
    if (sy > maxY) maxY = sy;
  }
  return { points, minX, maxX, minY, maxY };
}

// Apply a projected cell quad to a DOM halo element: translate to
// the AABB origin, size to the AABB, and clip-path to the actual
// four-corner polygon (in tl, tr, br, bl order). This keeps the
// halo locked to the grid lines under any camera angle. No AABB
// overshoot, no neighbour overlap.
export function applyCellQuadStyle(host, el, projected, pad = 1) {
  const { points, minX, minY, maxX, maxY } = projected;
  const ox = minX - pad;
  const oy = minY - pad;
  const w = Math.round(maxX - minX + pad * 2);
  const h = Math.round(maxY - minY + pad * 2);
  const clip = points
    .map((p) => `${(p.x - ox).toFixed(2)}px ${(p.y - oy).toFixed(2)}px`)
    .join(', ');
  el.style.display = 'block';
  el.style.transform = `translate(${Math.round(ox)}px, ${Math.round(oy)}px)`;
  el.style.width = `${w}px`;
  el.style.height = `${h}px`;
  el.style.clipPath = `polygon(${clip})`;
}

/**
 * Per-frame projector. Anchors each movement-reach halo at the
 * center of its cell. Mirrors updateCellTargetOverlays exactly
 * (same cell-corner projection, same data-hovered flag for the
 * cursor-cell brightening).
 *
 * Aura drags reuse the same halo system: every anchor's covered
 * cells are in the reach set, and `data-aura-active` flags the
 * cells of the cursor's nearest anchor so the player sees exactly
 * which cells the aura would land on if dropped.
 */
export function updateMovementReachOverlays(host) {
  if (host.movementReachOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();

  // Cursor world point. Drives both the per-cell hover flag (for
  // spawn/move halos) and the per-anchor selection (for aura halos).
  let cursorPoint = null;
  let hoveredKey = null;
  if (host.lastMouseEvent) {
    cursorPoint = host.scene.raycastTablePoint(host.lastMouseEvent);
    if (cursorPoint) {
      const cell = host._cellAtWorld(cursorPoint.x, cursorPoint.z);
      if (cell) hoveredKey = `${cell.col},${cell.row}`;
    }
  }

  // For each aura drag entry, pick the anchor closest to the cursor
  // and union its covered cells into `activeAuraCells`. Those cells
  // get `data-aura-active="true"`. The brighter "this is where the
  // aura would land" treatment. Cells belonging to OTHER anchors
  // stay in the reach set (rendered dim) so the player still sees
  // the full legal placement space.
  const activeAuraCells = new Set();
  if (cursorPoint && host.dragging?.reach) {
    for (const entry of host.dragging.reach.values()) {
      if (entry.kind !== 'aura') continue;
      const anchors = entry.anchors || [];
      let bestAnchor = null;
      let bestDist = Infinity;
      for (const a of anchors) {
        if (!a.point) continue;
        const dx = a.point.x - cursorPoint.x;
        const dz = a.point.z - cursorPoint.z;
        const d = dx * dx + dz * dz;
        if (d < bestDist) { bestDist = d; bestAnchor = a; }
      }
      if (bestAnchor?.coveredCells) {
        for (const key of bestAnchor.coveredCells) activeAuraCells.add(key);
      }
    }
  }

  for (const [cellKey, el] of host.movementReachOverlayRefs) {
    const [colStr, rowStr] = cellKey.split(',');
    const projected = host._projectCellQuad(grid, parseInt(colStr, 10), parseInt(rowStr, 10), camera, rect);
    if (!projected) { el.style.display = 'none'; continue; }
    host._applyCellQuadStyle(el, projected);
    if (cellKey === hoveredKey) el.setAttribute('data-hovered', 'true');
    else el.removeAttribute('data-hovered');
    if (activeAuraCells.has(cellKey)) el.setAttribute('data-aura-active', 'true');
    else el.removeAttribute('data-aura-active');
  }
}

// v1.72. Region-view cell list. Only emits when the player has
// toggled the subsurface view. One entry per subsurface cell. The
// connected-component scan is still used because Rubble / land sites
// break bodies (see regions.test.js) and a flat "is subsurface?"
// probe would ignore that. Each entry gets projected through
// `_projectCellQuad` so the tint traces the same grid quad the
// movement-reach halo uses.
export function buildRegionViewCells(host) {
  if (host.state.regionView !== 'subsurface') return [];
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return [];
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const getFullCard = host._getFullCard;

  const cells = [];
  const water = connectedSubsurfaceComponents(
    host.meshes, host._cellAtWorld, totalCols, totalRows, getFullCard, 'underwater',
  );
  for (const body of water) {
    for (const cellKey of body) {
      cells.push({ id: `water-${cellKey}`, kind: 'water', cellKey });
    }
  }
  const underground = connectedSubsurfaceComponents(
    host.meshes, host._cellAtWorld, totalCols, totalRows, getFullCard, 'underground',
  );
  for (const body of underground) {
    for (const cellKey of body) {
      cells.push({ id: `underground-${cellKey}`, kind: 'underground', cellKey });
    }
  }

  // Compute per-cell edge masks so two cardinally-adjacent cells of
  // the same kind drop the rim on their shared edge (the body reads
  // as one continuous shape rather than a row of fenced cells).
  // Two cells in this list are guaranteed to be in the same body
  // iff they're cardinally adjacent and same kind. Non-subsurface
  // cells (Rubble / land) aren't in the list at all, so they can't
  // appear between two same-kind cells here.
  const presentByKindKey = new Set(cells.map((c) => `${c.kind}|${c.cellKey}`));
  const hasSameKindNeighbor = (kind, col, row) =>
    presentByKindKey.has(`${kind}|${col},${row}`);
  for (const cell of cells) {
    const [colStr, rowStr] = cell.cellKey.split(',');
    const col = parseInt(colStr, 10);
    const row = parseInt(rowStr, 10);
    // edges: [top, right, bottom, left]. 1 = exposed (draw rim),
    // 0 = shared with same-kind neighbor (hide rim, fuse seamlessly).
    cell.edges = [
      hasSameKindNeighbor(cell.kind, col,     row - 1) ? 0 : 1,
      hasSameKindNeighbor(cell.kind, col + 1, row    ) ? 0 : 1,
      hasSameKindNeighbor(cell.kind, col,     row + 1) ? 0 : 1,
      hasSameKindNeighbor(cell.kind, col - 1, row    ) ? 0 : 1,
    ];
  }
  return cells;
}

export function buildCellPipEntries(host) {
  // Manual (automations-off) session: element affinity is the players'
  // bookkeeping. No engine-derived pip strips on the squares.
  if (host._automationsEnabled?.() === false) return [];
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return [];
  // Exclude any staged fresh-play drag so the cell the player is
  // hovering doesn't sprout the dragged site's affinity pips before
  // the player has actually released. Affinities register on commit
  // (applySitePlayEffects in handleMouseUp).
  const meshesForPips = host._meshesExcludingStagedDrag();
  const sitedCellKeys = cellsWithAnySite(meshesForPips, host._cellAtWorld);
  if (sitedCellKeys.size === 0) return [];

  const getFullCard = host._getFullCard;

  // v1.69. One strip per cell. Pips aggregate elements from EVERY
  // site in the cell regardless of who controls them. Position +
  // orientation is keyed to the LOCAL CAMERA's side so each player
  // always sees pips at their own near edge, upright for them. Uses
  // _localCameraRotated (not getActivePlayerRotated): in casual
  // sandbox the camera is fixed at P1's seat regardless of which
  // player's turn it is, so pips must anchor to the camera.
  const viewerRotated = host._localCameraRotated();
  const entries = [];
  for (const cellKey of sitedCellKeys) {
    const [colStr, rowStr] = cellKey.split(',');
    const col = parseInt(colStr, 10);
    const row = parseInt(rowStr, 10);
    const elements = elementsAtCell(meshesForPips, host._cellAtWorld, col, row, getFullCard);
    if (!elements.air && !elements.earth && !elements.fire && !elements.water) continue;
    entries.push({ cellKey, viewerRotated, elements });
  }
  return entries;
}

/**
 * Per-frame projector. Anchors each pip strip at the bottom-center
 * of its cell. Mirrors updateCellTargetOverlays: project the cell's
 * four corners, take the midpoint of the bottom edge, write a
 * translate3d transform. For rotated strips we layer the 180°
 * rotation ON TOP of the translate so the icons read upright for
 * the strip's owner.
 */
export function updateCellElementPipOverlays(host) {
  if (host.cellPipOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const v = host._hugVec;

  for (const [stripKey, el] of host.cellPipOverlayRefs) {
    const [cellKey, rotMarker] = stripKey.split('|');
    // v1.69. 'r' marker is now the VIEWER's rotation (host vs guest
    // perspective), not the site owner's. Both players see one strip
    // per cell at THEIR bottom edge, oriented upright FOR THEM.
    const viewerRotated = rotMarker === 'r';
    const [colStr, rowStr] = cellKey.split(',');
    const col = parseInt(colStr, 10);
    const row = parseInt(rowStr, 10);
    const geo = sampleCellGeometry(grid, col, row);
    if (!geo) { el.style.display = 'none'; continue; }

    // Anchor at the WORLD edge that's nearest to the viewer's camera.
    // P1 sits at the bl/br edge of the table → that's their near edge,
    // and the cell's projected bl/br lands at the bottom of P1's
    // screen. P2's camera is rotated 180°, so the cell's world tl/tr
    // edge becomes P2's near edge and projects to the bottom of P2's
    // screen. Picking the right world edge per viewer is what makes
    // the strip live at "the bottom of MY square" for each player.
    const wx = viewerRotated ? (geo.tl.x + geo.tr.x) / 2 : (geo.bl.x + geo.br.x) / 2;
    const wz = viewerRotated ? (geo.tl.z + geo.tr.z) / 2 : (geo.bl.z + geo.br.z) / 2;
    v.set(wx, 0.18, wz).project(camera);
    const sx = ((v.x + 1) / 2) * rect.width;
    const sy = ((-v.y + 1) / 2) * rect.height;

    el.style.display = 'flex';
    // Lift the strip INWARD from the anchor. `translate(-50%, -100%)`
    // bottom-aligns the strip on (sx, sy), so it extends upward in
    // screen space (i.e. into the cell, away from the near edge).
    // Same shift for both viewers. The anchor swap above is what
    // turns "near-edge for P1" into "near-edge for P2".
    el.style.transform =
      `translate(${Math.round(sx)}px, ${Math.round(sy)}px)`
      + ` translate(-50%, -100%)`;
  }
}

/**
 * Per-frame projector for every pending card's marching-ants outline
 * and "Equip to" label. Mirrors updateHoverStatusOverlayPosition's
 * canvas-local-coords approach: project the mesh's 8-corner bounding
 * box, take the screen-space rect, and write it directly into the
 * DOM transform / width / height.
 */
export function updateEquipPendingOverlays(host) {
  if (host.equipOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();

  for (const [cardId, refs] of host.equipOverlayRefs) {
    const mesh = host.meshes.get(cardId);
    if (!mesh) {
      if (refs.label) refs.label.style.display = 'none';
      continue;
    }
    const box = host._hugBox.setFromObject(mesh);
    if (!isFinite(box.min.x) || box.isEmpty()) continue;

    const v = host._hugVec;
    const mn = box.min;
    const mx = box.max;
    const corners = [
      [mn.x, mn.y, mn.z], [mx.x, mn.y, mn.z],
      [mn.x, mx.y, mn.z], [mx.x, mx.y, mn.z],
      [mn.x, mn.y, mx.z], [mx.x, mn.y, mx.z],
      [mn.x, mx.y, mx.z], [mx.x, mx.y, mx.z],
    ];
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    // Canvas-local pixel coords. See the matching note in
    // updateHoverStatusOverlayPosition. Adding rect.left/top would
    // misalign the marching-ants outline when the canvas is
    // letterboxed in the viewport.
    for (const [x, y, z] of corners) {
      v.set(x, y, z).project(camera);
      const sx = ((v.x + 1) / 2) * rect.width;
      const sy = ((-v.y + 1) / 2) * rect.height;
      if (sx < minX) minX = sx;
      if (sx > maxX) maxX = sx;
      if (sy < minY) minY = sy;
      if (sy > maxY) maxY = sy;
    }
    const pad = 4;
    if (refs.label) {
      refs.label.style.display = 'block';
      const centerX = (minX + maxX) / 2;
      // Sit the label just above the outline (pad + ~10px gap), with
      // a -50% X / -100% Y inner shift so the label's bottom-center
      // anchors to that point.
      refs.label.style.transform = `translate(${Math.round(centerX)}px, ${Math.round(minY - pad - 10)}px) translate(-50%, -100%)`;
    }
  }
}


/**
 * Per-frame projector for the gold pulsing halos drawn over every
 * valid cell while a two-step location effect is pending. Projects
 * each cell's four corners to screen space and writes the bounding
 * rect into the halo div. Cells that aren't on the current pending
 * target's valid set are skipped (their refs aren't even registered).
 */
export function updateCellTargetOverlays(host) {
  if (host.cellTargetOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();

  // Cell under the cursor. Used to flag the hovered halo with
  // data-hovered="true" so CSS can boost its glow (v1.18b). We
  // intentionally do NOT use the React hover system (host.hoveredMesh
  // is suppressed during cell-pick) so the cell halo is the only
  // hover affordance the player sees.
  let hoveredKey = null;
  if (host.lastMouseEvent) {
    const point = host.scene.raycastTablePoint(host.lastMouseEvent);
    if (point) {
      const cell = host._cellAtWorld(point.x, point.z);
      if (cell) hoveredKey = `${cell.col},${cell.row}`;
    }
  }

  for (const [cellKey, el] of host.cellTargetOverlayRefs) {
    const [colStr, rowStr] = cellKey.split(',');
    const projected = host._projectCellQuad(grid, parseInt(colStr, 10), parseInt(rowStr, 10), camera, rect);
    if (!projected) { el.style.display = 'none'; continue; }
    host._applyCellQuadStyle(el, projected);
    // v1.18b: brighten the cell the cursor is over so the player
    // gets feedback on which cell will be picked. CSS reads
    // [data-hovered="true"] on .cell-target-halo to apply the
    // boosted styling.
    if (cellKey === hoveredKey) el.setAttribute('data-hovered', 'true');
    else el.removeAttribute('data-hovered');
  }
}

// Replay feed hover: per-frame projector for the quiet quads over the
// cells of the cards a hovered chronicle row mentions. Same projection
// as updateCellTargetOverlays, no hover boost.
export function updateCellMentionOverlays(host) {
  if (host.cellMentionOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  for (const [cellKey, el] of host.cellMentionOverlayRefs) {
    const [colStr, rowStr] = cellKey.split(',');
    const projected = host._projectCellQuad(grid, parseInt(colStr, 10), parseInt(rowStr, 10), camera, rect);
    if (!projected) { el.style.display = 'none'; continue; }
    host._applyCellQuadStyle(el, projected);
  }
}

// v1.111. Flamecaller's projectile flight visualizer. Mirrors
// updateCellTargetOverlays for per-frame positioning, but reads
// pending paths from pendingCellTarget.projectilePaths and toggles
// data-hovered="true" on every halo whose direction matches the
// direction containing the hovered cell. So the whole line of
// flight brightens at once.
export function updateProjectilePathOverlays(host) {
  if (host.projectilePathOverlayRefs.size === 0) return;
  if (!host.scene?.camera || !host.canvasRef.current) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const camera = host.scene.camera;
  const rect = host.canvasRef.current.getBoundingClientRect();
  const paths = host.state.pendingCellTarget?.projectilePaths || [];

  // Identify the direction the cursor currently belongs to (if any)
  // so all halos in that direction can glow together.
  let hoveredDirection = null;
  if (host.lastMouseEvent) {
    const point = host.scene.raycastTablePoint(host.lastMouseEvent);
    if (point) {
      const cell = host._cellAtWorld(point.x, point.z);
      if (cell) {
        for (const path of paths) {
          if (path.pathCells.some((c) => c.col === cell.col && c.row === cell.row)) {
            hoveredDirection = path.direction;
            break;
          }
        }
      }
    }
  }

  for (const [refKey, el] of host.projectilePathOverlayRefs) {
    const [dir, cellKey] = refKey.split('|');
    const [colStr, rowStr] = cellKey.split(',');
    const projected = host._projectCellQuad(
      grid, parseInt(colStr, 10), parseInt(rowStr, 10), camera, rect,
    );
    if (!projected) { el.style.display = 'none'; continue; }
    host._applyCellQuadStyle(el, projected);
    if (dir === hoveredDirection) el.setAttribute('data-hovered', 'true');
    else el.removeAttribute('data-hovered');
  }
}

// v1.104. Single-cell red halo over the cursor when the hovered
// cell is OUTSIDE the valid set for whatever interaction is active
// (spawn drag, move drag, cell-pick prompt, summon placement). Hides
// itself when no interaction is active OR the hover lands on a
// legal cell.
//
// We union every potentially-active valid set so the red dot only
// fires when ALL of them say "not valid here." Drags typically have
// one card's reach set; multi-card drags union to a permissive set
// (we'd rather under-warn than over-warn).
export function updateInvalidCellHoverOverlay(host) {
  const el = host._invalidCellHoverRef;
  if (!el) return;
  if (!host.scene?.camera || !host.canvasRef.current) { el.style.display = 'none'; return; }
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) { el.style.display = 'none'; return; }

  // Gather valid sets from every active interaction. The cell is
  // invalid only if at least one interaction is active AND none of
  // them accept the hovered cell.
  const validSets = [];
  if (host.state.pendingCellTarget?.validCells) {
    validSets.push(host.state.pendingCellTarget.validCells);
  }
  const summonHead = host.state.pendingSummonPlacement?.queue?.[0];
  if (summonHead?.validCells) validSets.push(summonHead.validCells);
  if (host.dragging?.reach) {
    for (const entry of host.dragging.reach.values()) {
      if (entry?.cells && entry.cells.size > 0) validSets.push(entry.cells);
      else if (entry?.kind === 'move' && entry.reach && host._automationsEnabled()) {
        // Unit move drag: a square beyond the unit's step reach is a
        // likely-illegal drop. It still commits (the server transcribes it
        // as a questionable manual reposition), so the red halo is a
        // warning, not a block. The origin cell is always fine (same-cell
        // repositioning). Manual sessions paint no legality UI, matching
        // buildMovementReachCells.
        const cells = cellsReachable(entry.reach);
        if (entry.fromCell) cells.add(`${entry.fromCell.col},${entry.fromCell.row}`);
        validSets.push(cells);
      }
    }
  }
  // A tapped / summoning-sick dragged unit has no reach entry at all
  // (skipped in _computeDragReach): only its own cell is a rules-legal
  // drop, so every cell-cross hover warns red. Same advisory-not-blocking
  // deal as above.
  if (host.dragging?.members && host._automationsEnabled()) {
    for (const m of host.dragging.members) {
      const card = m.cardInstance;
      if (!card || host.dragging.freshPlayIds?.has(card.id)) continue;
      if (!countsAsUnit(card)) continue;
      if (!card.tapped && !hasSummoningSickness(card)) continue;
      const origin = card.cell
        || host._cellAtWorld(m.initialX ?? 0, m.initialZ ?? 0);
      if (origin) validSets.push(new Set([`${origin.col},${origin.row}`]));
    }
  }
  if (validSets.length === 0) { el.style.display = 'none'; return; }

  if (!host.lastMouseEvent) { el.style.display = 'none'; return; }
  const point = host.scene.raycastTablePoint(host.lastMouseEvent);
  if (!point) { el.style.display = 'none'; return; }
  const cell = host._cellAtWorld(point.x, point.z);
  if (!cell) { el.style.display = 'none'; return; }
  const key = `${cell.col},${cell.row}`;
  const isValid = validSets.some((set) => set.has(key));
  if (isValid) { el.style.display = 'none'; return; }

  const rect = host.canvasRef.current.getBoundingClientRect();
  const projected = host._projectCellQuad(grid, cell.col, cell.row, host.scene.camera, rect);
  if (!projected) { el.style.display = 'none'; return; }
  host._applyCellQuadStyle(el, projected);
}
